import { Scene } from 'phaser';
import { GameSession, SESSION_KEY } from '../game/session';

export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;

export const BACKGROUND_LAYERS = [
  'bg-1-basecolor',
  'bg-2-air',
  'bg-3-airyobjects',
  'bg-4-solidobjects',
] as const;

export const CURSORS = {
  player_1: 'url(assets/cursors/1p-cursor_32x32.png), pointer',
  player_2: 'url(assets/cursors/2p-cursor_32x32.png), pointer',
  generic: 'url(assets/cursors/generic-cursor_32x32.png), pointer',
} as const;

export const TEXT_STYLES = {
  menu: {
    fontFamily: 'ARCO_Typography',
    fontSize: '38px',
    color: '#ffffff',
  },
  characterName: {
    fontFamily: 'ARCO_Typography',
    fontSize: '26px',
    color: '#483B30',
  },
  hint: {
    fontFamily: 'ARCO_Typography',
    fontSize: '12px',
    color: '#ffffff',
  },
} as const;

/** The theme that plays under the intro and every menu screen. */
export const MENU_TRACK = 'menu_2.mp3';

/** Adds shared session access and the parallax backdrop every screen uses. */
export abstract class KoodooScene extends Scene {
  get session(): GameSession {
    const session = this.registry.get(SESSION_KEY) as GameSession | undefined;
    if (!session) {
      throw new Error(
        `No GameSession in the registry. Set registry key "${SESSION_KEY}" before starting scenes.`,
      );
    }
    return session;
  }

  /**
   * Keeps the menu theme going under a menu screen. Safe to call on every
   * entry: it never restarts or doubles a theme that is already playing, and
   * it brings the theme back after a race has silenced it.
   */
  protected playMenuTheme(): void {
    this.session.audio.ensureMusic(MENU_TRACK);
  }

  /** Queues the background layers. Safe to call from any scene's `preload`. */
  protected loadBackgrounds(layers: readonly string[] = BACKGROUND_LAYERS): void {
    for (const layer of layers) {
      this.load.image(layer, `assets/images/backgrounds/${layer}.png`);
    }
  }

  /**
   * Draws the backdrop and returns the two layers that rotate slowly, which
   * callers spin in their own `update`.
   */
  protected addBackgrounds(layers: readonly string[] = BACKGROUND_LAYERS) {
    const images = layers.map((layer) =>
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, layer),
    );
    return { air: images[1], airyObjects: images[2] };
  }

  protected setCursor(cursor: keyof typeof CURSORS): void {
    this.input.setDefaultCursor(CURSORS[cursor]);
  }
}
